/** The console agent (backend metaplan A9; AIE §10, Q6) — the AI notification-editing chat.
 *
 *  - `GET  /tour_operators/{id}/ai_agent/` — the launch context on one tenant: models, caps,
 *    whether the agent can run for the caller (`blockers`) and what narrows it (`notes`).
 *  - `POST /tour_operators/{id}/ai_agent/` — one message: a `console_agent` background task
 *    (202 `{task_id}`; 409 carries the running turn's `task_id` to adopt). Poll
 *    `/bgtasks/{id}/`, read `/bgtasks/{id}/result/` (`ConsoleAgentTurnResult`).
 *
 *  The agent proposes only: what it asks to apply waits as a pending change on the review page
 *  (`/ai_changes/...`, `@/api/aiEdit`) for a person holding `console_notification_mcp_write`. */
import { apiGet, apiPost } from './client';
import type { components } from './types';

type Schemas = components['schemas'];

export type ConsoleAgentLaunchContext = Schemas['ConsoleAgentLaunchContext'];
export type ConsoleAgentNotice = Schemas['ConsoleAgentNotice'];
export type ConsoleAgentStartRequest = Schemas['ConsoleAgentStartRequest'];
export type ConsoleAgentTurnResult = Schemas['ConsoleAgentTurnResult'];
export type ConsoleAgentToolCall = Schemas['ConsoleAgentToolCall'];

/** The task type of a turn (bgtasks listing). */
export const CONSOLE_AGENT_TASK_TYPE = 'console_agent';

export function launchContext(topId: number): Promise<ConsoleAgentLaunchContext> {
  return apiGet(`/tour_operators/${topId}/ai_agent/`);
}

export function startTurn(
  topId: number,
  body: ConsoleAgentStartRequest,
): Promise<{ task_id: string; queued: boolean }> {
  return apiPost(`/tour_operators/${topId}/ai_agent/`, body);
}

/** The bgtask label token the backend stamps (`AI agent [tenant:<id>]`): narrows the thread
 *  listing without small-int substring collisions. */
export function tenantLabelToken(topId: number): string {
  return `[tenant:${topId}]`;
}

export const consoleAgentKeys = {
  all: ['consoleAgent'] as const,
  context: (topId: number) => ['consoleAgent', 'context', topId] as const,
};
